// Spend record — the uniform shape every model-token call is metered into
// (analytics+postgres Phase 9). One `SpendRecord` per billable model call, at
// every surface:
//
//   - `coding`   — the sandbox `claude` CLI: one row per model call through
//                  the model gateway (`SANDBOX_GATEWAY_WORKFLOW_ID`).
//   - `pm`       — the in-process servant turn (`servant/agent.ts`).
//   - `handoff`  — the boot→ready transcript summary (`orchestrator/handoff.ts`).
//   - `workflow` — a `@loom/workflows` llm-helper call (the DoR/quality/parse/
//                  rank/render/generate calls), captured per-call (incl. retries)
//                  via the `spendContext` AsyncLocalStorage in `@loom/workflows`.
//
// Identity (`sessionId`/`userId`/`projectId`) is always resolvable at the seam
// that emits the record; USD is NOT stored here — it is derived from
// `@loom/shared/pricing` over the raw token counts at projection time, so a
// price change re-prices history.
//
// Pure types only (no `node:async_hooks`, no `ai` import) so this subpath is
// safe to import from the web bundle. The AsyncLocalStorage instance lives in
// `@loom/workflows` (server-only); the structural usage extractor below takes
// an already-resolved AI-SDK usage object so it needs no `ai` dependency.

/** The surface a spend record was incurred on. */
export const SPEND_SURFACES = ["coding", "pm", "handoff", "workflow"] as const;
export type SpendSurface = (typeof SPEND_SURFACES)[number];

/**
 * Where a ledger row's canonical `cost_usd` came from.
 *
 *   - `priced`   — raw tokens × `@loom/shared/pricing`, re-derived every fold.
 *   - `repriced` — the source's own USD figure moved onto the rate Anthropic
 *                  bills (`correctReportedCost`); `reported_cost_usd` keeps the
 *                  source figure verbatim, so the correction is one UPDATE from
 *                  being reversed. No writer emits this any more either; rows
 *                  an earlier sync wrote still read.
 *   - `reported` — the source figure verbatim. No writer emits this any more;
 *                  it survives so rows written before the correction still read.
 *
 * Ordered so a plain `max()` over a group yields the strongest claim present
 * (`repriced` > `reported` > `priced`), which is how the per-model breakdown
 * aggregates provenance in both query backends.
 */
export const SPEND_COST_SOURCES = ["priced", "reported", "repriced"] as const;
export type SpendCostSource = (typeof SPEND_COST_SOURCES)[number];

/** Token counts for one model call. Cache fields are 0 when the provider /
 *  transport doesn't surface a prompt-cache breakdown (e.g. the `claude` CLI). */
export interface ModelUsage {
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheCreationTokens: number;
  readonly cacheReadTokens: number;
  /** Of `cacheCreationTokens`, the ones written at the 1-hour TTL. Absent: 0,
   *  every write priced at the 5-minute rate. */
  readonly cacheCreation1hTokens?: number;
  /** Server-side web searches the call ran. Absent: 0. */
  readonly webSearchRequests?: number;
}

/** Per-model usage as reported by a source that meters several models in one
 *  session — the shape of the `claude` CLI's `modelUsage` map entries, carried
 *  on the coding bridge's persist frames. `reportedCostUsd` is the source's own
 *  USD figure (the CLI's `costUSD`), preferred over table-derived pricing at
 *  projection time; the 5m/1h cache-write TTL split feeds the dual-rate
 *  pricing fallback when the reported figure is absent. */
export interface ReportedModelUsage extends ModelUsage {
  readonly cacheCreation5mTokens?: number;
  readonly cacheCreation1hTokens?: number;
  readonly reportedCostUsd?: number;
}

/** Model id → usage for one coding session — the CLI's `modelUsage` shape. */
export type ModelUsageMap = Readonly<Record<string, ReportedModelUsage>>;

/** A single metered model call. */
export interface SpendRecord {
  /** Raw Loom session id (NOT the Firestore doc-id form). */
  readonly sessionId: string;
  /** GitLab numeric user id, as a string (matches `sessions_index` keying). */
  readonly userId: string;
  /** GitLab project id. */
  readonly projectId: number;
  /** GitLab namespace path of the project, when known at the seam (lets the
   *  sync resolve `dim_project` without a join back to `sessions_index`). */
  readonly projectPath?: string;
  readonly surface: SpendSurface;
  /** Workflow id for `surface === "workflow"`, else null. */
  readonly workflowId: string | null;
  /** Resolved model id, for pricing + retroactive re-pricing. */
  readonly modelId: string;
  /** The saved key the call ran on (`app.anthropic_keys.key_id`): `apikey_…`
   *  for a key from Loom's org, a uuid for one pasted from another; null for
   *  Loom's env keys. */
  readonly keyId: string | null;
  /** Wall-clock at capture (ms since epoch). */
  readonly occurredAtMs: number;
  /** 0-based attempt index — retried llm-helper calls each emit a record. */
  readonly attemptIndex: number;
  readonly usage: ModelUsage;
  /** Transcribed audio seconds, for a call priced per audio second (the
   *  meeting transcription; `usage` is then all zeros). Absent on
   *  every token-priced call. */
  readonly audioSeconds?: number;
}

/** The `workflowId` of a model gateway row (`surface: "coding"`): one
 *  sandbox call, metered as it passed through the server
 *  (`apps/server/src/model-gateway/`). A `coding` row without it is the
 *  per-session figure the `claude` CLI reported. */
export const SANDBOX_GATEWAY_WORKFLOW_ID = "sandbox_gateway";

/** True for a model gateway row. */
export const isSandboxGatewaySpend = (row: {
  readonly surface: string;
  readonly workflowId: string | null;
}): boolean => row.surface === "coding" && row.workflowId === SANDBOX_GATEWAY_WORKFLOW_ID;

/** Fire-and-forget sink. Implementations MUST NOT throw and MUST NOT block the
 *  caller on I/O (the seams are on the model-turn hot path). */
export type SpendSink = (record: SpendRecord) => void;

const num = (v: unknown): number =>
  typeof v === "number" && Number.isFinite(v) ? v : 0;

/** The structural slice of an AI-SDK v5 `LanguageModelUsage` the spend seams
 *  read. `inputTokens` is the UNCACHED prompt portion (the API's raw
 *  `input_tokens`); cache reads ride `cachedInputTokens`. */
export interface UsageLike {
  readonly inputTokens?: unknown;
  readonly outputTokens?: unknown;
  readonly cachedInputTokens?: unknown;
}

/**
 * Structural extraction of a {@link ModelUsage} from an AI-SDK v5 result's
 * (already-resolved) `usage` + provider metadata. Reads `inputTokens` /
 * `outputTokens` / `cachedInputTokens` off the usage, and the anthropic
 * cache-write count off `providerMetadata.anthropic.cacheCreationInputTokens`
 * (v5 keeps cache WRITES only in provider metadata; cache READS moved onto
 * the usage object). Takes plain values so it needs no `ai` import (the
 * caller awaits the promises `streamText` exposes before calling).
 */
export const toModelUsage = (
  usage: UsageLike | undefined,
  providerMetadata?: unknown,
): ModelUsage => {
  const anthropic =
    providerMetadata !== null &&
    typeof providerMetadata === "object" &&
    "anthropic" in providerMetadata
      ? (providerMetadata as { anthropic?: unknown }).anthropic
      : undefined;
  const cache =
    anthropic !== null && typeof anthropic === "object"
      ? (anthropic as {
          cacheCreationInputTokens?: unknown;
          cacheReadInputTokens?: unknown;
        })
      : {};
  return {
    inputTokens: num(usage?.inputTokens),
    outputTokens: num(usage?.outputTokens),
    cacheCreationTokens: num(cache.cacheCreationInputTokens),
    // v5 reports cache reads on the usage object; tolerate the legacy
    // provider-metadata location for any stored/replayed older shape.
    cacheReadTokens:
      num(usage?.cachedInputTokens) || num(cache.cacheReadInputTokens),
  };
};

/** True when a usage record carries no tokens at all — callers skip emitting an
 *  empty record (e.g. a model call that produced nothing). */
export const isEmptyUsage = (u: ModelUsage): boolean =>
  u.inputTokens === 0 &&
  u.outputTokens === 0 &&
  u.cacheCreationTokens === 0 &&
  u.cacheReadTokens === 0 &&
  (u.webSearchRequests ?? 0) === 0;
